import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { AuthProvider, homeFor, useAuth } from "@/context/AuthContext";
import { BusinessProvider } from "@/context/BusinessContext";
import { formatApiError } from "@/lib/api";
import LandingPage from "@/pages/LandingPage";
import CustomerLogin from "@/pages/CustomerLogin";
import StaffLogin from "@/pages/StaffLogin";
import CustomerPortal from "@/pages/CustomerPortal";
import JobEntryPage from "@/pages/JobEntryPage";
import AdminDashboard from "@/pages/AdminDashboard";

const Loading = () => (
  <div className="min-h-screen grid place-items-center bg-[#030712] font-mono text-cyan-400 text-sm tracking-widest blink-caret" data-testid="auth-loading">
    LOADING
  </div>
);

function Guard({ children, roles }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to={pathname.startsWith("/portal") ? "/login" : "/staff/login"} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user)} replace />;
  return children;
}

function AuthCallback() {
  const { loginWithSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) {
      return;
    }
    done.current = true;
    const sessionId = new URLSearchParams(location.hash.slice(1)).get("session_id");
    window.history.replaceState(null, "", window.location.pathname);
    loginWithSession(sessionId)
      .then((u) => navigate(homeFor(u), { replace: true }))
      .catch((e) => {
        toast.error(formatApiError(e));
        navigate("/login", { replace: true });
      });
  }, [location.hash, loginWithSession, navigate]);

  return <Loading />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<CustomerLogin />} />
      <Route path="/staff/login" element={<StaffLogin />} />
      <Route path="/portal" element={<Guard roles={["customer"]}><CustomerPortal /></Guard>} />
      <Route path="/new-job" element={<Guard roles={["admin", "employee"]}><JobEntryPage /></Guard>} />
      <Route path="/admin" element={<Guard roles={["admin"]}><AdminDashboard /></Guard>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BusinessProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-center" theme="dark" richColors />
      </AuthProvider>
    </BusinessProvider>
  );
}

export default App;
